import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import { formatPhone } from "../MessagesList/mentions";

// Lista de participantes que abre ao digitar "@" num grupo, no padrão do WhatsApp Web.
const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  const soft = isDark ? "#8696a0" : "#667781";
  return {
    list: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: "calc(100% + 8px)",
      zIndex: 20,
      maxHeight: 268,
      margin: 0,
      padding: "6px 0",
      overflowY: "auto",
      listStyle: "none",
      borderRadius: 12,
      backgroundColor: isDark ? "#233138" : "#ffffff",
      color: isDark ? "#e9edef" : "#111b21",
      boxShadow: "0 2px 5px rgba(11,20,26,0.26), 0 2px 10px rgba(11,20,26,0.16)",
      ...theme.scrollbarStyles,
    },
    item: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minHeight: 52,
      padding: "6px 14px",
      cursor: "pointer",
      userSelect: "none",
    },
    active: {
      backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(11,20,26,0.06)",
    },
    avatar: {
      flex: "none",
      width: 38,
      height: 38,
      borderRadius: "50%",
      overflow: "hidden",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: isDark ? "#6a7175" : "#dfe5e7",
      color: isDark ? "#e9edef" : "#54656f",
      fontSize: 16,
      fontWeight: 600,
      "& img": { width: "100%", height: "100%", objectFit: "cover", display: "block" },
    },
    text: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      lineHeight: "19px",
    },
    name: {
      fontSize: 15,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    sub: {
      fontSize: 12.5,
      color: soft,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    admin: {
      flex: "none",
      padding: "1px 7px",
      borderRadius: 8,
      fontSize: 11,
      fontWeight: 500,
      color: isDark ? "#06cf9c" : "#008069",
      backgroundColor: isDark ? "rgba(6,207,156,0.14)" : "rgba(0,128,105,0.1)",
    },
  };
});

const Avatar = ({ participant, className }) => {
  const [failed, setFailed] = useState(false);
  const initial = (participant.name || "").trim().charAt(0).toUpperCase() || "#";
  return (
    <span className={className} aria-hidden="true">
      {participant.profilePicUrl && !failed ? (
        <img src={participant.profilePicUrl} alt="" onError={() => setFailed(true)} />
      ) : (
        initial
      )}
    </span>
  );
};

const MentionSuggest = ({ suggestions, activeIndex, onHover, onChoose }) => {
  const classes = useStyles();
  const listRef = useRef(null);

  useEffect(() => {
    const list = listRef.current;
    const active = list && list.children[activeIndex];
    if (active && active.scrollIntoView) active.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <ul ref={listRef} className={classes.list} role="listbox" aria-label={i18n.t("conversation.mentions.listLabel")} data-mention-suggest="true">
      {suggestions.map((participant, i) => {
        const phone = formatPhone(participant.number);
        return (
          <li
            key={participant.id}
            role="option"
            aria-selected={i === activeIndex}
            className={clsx(classes.item, i === activeIndex && classes.active)}
            onMouseEnter={() => onHover(i)}
            // mousedown: escolhe sem tirar o foco do campo (no toque vira "tap")
            onMouseDown={(e) => {
              e.preventDefault();
              onChoose(participant);
            }}
          >
            <Avatar participant={participant} className={classes.avatar} />
            <span className={classes.text}>
              <span className={classes.name}>{participant.name || phone || i18n.t("conversation.mentions.participant")}</span>
              {(participant.name ? phone : !phone) && (
                <span className={classes.sub}>{participant.name ? phone : i18n.t("conversation.mentions.noNumber")}</span>
              )}
            </span>
            {participant.isAdmin && <span className={classes.admin}>{i18n.t("conversation.mentions.admin")}</span>}
          </li>
        );
      })}
    </ul>
  );
};

export default MentionSuggest;
